import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ReactNode, useEffect } from 'react';
import { convertToDotString } from '@diritto/common/utils/date';
import { EventDto } from '@diritto/common/event/dtos/event.dto';
import { showAlertModal } from 'utils/modal';
import { EventProgressType, EventProgressTypeString } from '@diritto/common/event/constants';

interface EventHeaderProps {
  className?: string;
  children?: ReactNode;
  event: EventDto;
  backgroundColor?: string;
}

export const EventHeader = ({ className, children, event, backgroundColor = Color.White }: EventHeaderProps) => {
  const { title, startAt, endAt, isInfinite, eventProgressType } = event;
  const date = isInfinite
    ? '상시 진행'
    : `${convertToDotString(new Date(startAt))} ~ ${convertToDotString(new Date(endAt))}`;

  useEffect(() => {
    switch (eventProgressType) {
      case EventProgressType.Ended:
        showAlertModal('종료된 이벤트예요.');
        break;
      default:
        break;
    }
  }, [eventProgressType]);

  return (
    <StylesContainer className={className} backgroundColor={backgroundColor}>
      <StylesProgressBadge
        isProgress={[EventProgressType.BeforeTheStart, EventProgressType.Proceeding].includes(eventProgressType)}>
        {EventProgressTypeString[eventProgressType]}
      </StylesProgressBadge>
      <StylesTitle>{title}</StylesTitle>
      <StylesDate>{date}</StylesDate>
      <StylesContent>{children}</StylesContent>
    </StylesContainer>
  );
};

const StylesContainer = styled.section(
  ({ backgroundColor }: { backgroundColor: string }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    background-color: ${backgroundColor};
  `,
);

const StylesProgressBadge = styled.p(
  ({ isProgress }: { isProgress: boolean }) => css`
    ${Typography.XXXSmall}
    flex-grow: 0;
    width: fit-content;
    background-color: ${isProgress ? Color.Primary500 : Color.Gray400};
    color: ${Color.White};
    padding: 3px 6px;
    border-radius: 100px;
  `,
);

const StylesTitle = styled.p`
  ${Typography.XLarge}
  font-weight: ${FontWeight.Bold};
  margin-top: 8px;
  padding: 0 16px;
`;

const StylesDate = styled.p`
  ${Typography.XXSmall}
  color: ${Color.Gray500};
  margin-top: 8px;
`;

const StylesContent = styled.div`
  width: 100%;
  margin-top: 24px;
`;
